import { Controller } from "react-hook-form";
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import locale from "@bonliva-crm/locale";
import {
  ConsultantEventLog_GetEvents,
  ConsultantSearchCard_Get,
  useConsultantQualityItemEditor_SetApprovedMutation,
  useSaveHSAIDParamsMutation,
  useConsultantQualityItemEditor_SetPausedMutation,
  useConsultantQualityItemEditor_ReactivateMutation,
  ConsultantQuality_GetConsultantNew
} from "@bonliva-crm/graphql";
import { ErrorCard, Spinner, SpinnerOverlay } from "@components";
import { Button, Typography, Grid, FormControlLabel, Checkbox } from "@material-ui/core";
import {
  SingleSelect,
  FileUpload,
  TextField,
  DatePicker,
  useSnackbarsPipeline,
  useAlertDialog,
  Color
} from "@bonliva-crm/ui";
import { canPauseQualityItem } from "@bonliva-crm/shared";
import { DevTool } from "@hookform/devtools";
import { cardTypeOptions, schema, FormData } from "./form";
import { useQualityItemEditor } from "../../quality-item-editor";
import { hasStatusParams, hasPausedState } from "../utils";

type Props = {
  consultantId: string;
  country: string;
  itemName: string;
  itemType: string;
  onClose?: () => any;
};

export const HSAIDQualityItemEditor: React.FC<Props> = (props) => {
  const classes = useStyles({});
  const { onClose } = props;
  const alert = useAlertDialog();

  const { showError, showSuccess } = useSnackbarsPipeline();

  const {
    form,
    loading,
    setLoading,
    data,
    error,
    fileUploader,
    clearStatus,
    withDocument,
    name
  } = useQualityItemEditor<FormData>({
    country: props.country,
    consultantId: props.consultantId,
    itemName: props.itemName,
    onClose,
    schema,
    getStatusParams(status) {
      const params: Partial<FormData> = {};

      if (hasStatusParams(status)) {
        params.cardType = status.params.cardType
          ? {
              label: locale.cardTypes[status.params.cardType],
              value: status.params.cardType
            }
          : null;
        params.cardNumber = status.params.cardNumber;
      }

      params.sendEmail = false;

      return params;
    }
  });

  const [_saveParams] = useSaveHSAIDParamsMutation();
  const [_setApproved] = useConsultantQualityItemEditor_SetApprovedMutation();
  const [_setPausedStatus] = useConsultantQualityItemEditor_SetPausedMutation();
  const [_reactivateQualityItem] = useConsultantQualityItemEditor_ReactivateMutation();

  const cardNumber = form.watch("cardNumber");
  const comment = form.watch("comment");
  const cardType = form.watch("cardType");
  const sendEmail = form.watch("sendEmail");

  const cardNumberState = form.getFieldState("cardNumber");
  const commentState = form.getFieldState("comment");
  const cardTypeState = form.getFieldState("cardType");
  const dateState = form.getFieldState("date");

  const dataStatus = data?.Status;
  const isApproved = ["Approved", "ApprovedWithComment"].includes(dataStatus?.Name);
  const isPaused = hasPausedState(dataStatus) && dataStatus.pausedState.isPaused;
  const canPause = !isPaused && canPauseQualityItem(props.itemName, dataStatus?.Name);

  const canSave =
    isApproved &&
    form.formState.isValid &&
    (cardTypeState.isDirty ||
      commentState.isDirty ||
      cardNumberState.isDirty ||
      dateState.isDirty);

  const saveParams = () => {
    if (!canSave) {
      return;
    }

    return _saveParams({
      variables: {
        consultantId: props.consultantId,
        country: props.country,
        hsaidNumber: comment,
        cardNumber: cardNumber,
        cardType: cardType?.value,
        setOn: form.getValues().date
      },
      refetchQueries: [
        ConsultantEventLog_GetEvents.OperationName,
        ConsultantSearchCard_Get.OperationName
      ],
      onCompleted: () => {
        showSuccess(locale.Saved);
        onClose();
      },
      onError: (error) => {
        showError(error.message, locale.ErrorOccurred);
      }
    });
  };

  const setApproved = () => {
    setLoading(true);

    const promise = () => {
      return _setApproved({
        variables: {
          consultantId: props.consultantId,
          country: props.country,
          qualityCheckName: props.itemName,
          setOn: form.getValues().date,
          comment,
          params: { cardType: cardType.value, cardNumber },
          sendEmail
        },
        refetchQueries: [
          ConsultantEventLog_GetEvents.OperationName,
          ConsultantSearchCard_Get.OperationName,
          ConsultantQuality_GetConsultantNew.OperationName
        ],
        onCompleted: () => {
          showSuccess(locale.Saved);
          setLoading(false);
          onClose();
        },
        onError: (error) => {
          showError(error.message, locale.ErrorOccurred);
          setLoading(false);
        }
      });
    };

    if (
      cardTypeState.isDirty &&
      (cardType.value === "OrdinaryCardBonliva" ||
        cardType.value === "ReserveCardBonliva")
    ) {
      alert
        .confirm({
          title: locale.AreYouSure,
          description: locale.hsaidApprovalConfirmation
        })
        .then((res) => {
          if (!res) {
            return;
          }
          return promise();
        });
    } else {
      return promise();
    }
  };

  const setPaused = () => {
    setLoading(true);

    return _setPausedStatus({
      variables: {
        consultantId: props.consultantId,
        country: props.country,
        qualityCheckName: props.itemName,
        setOn: form.getValues().date || new Date()
      },
      refetchQueries: [
        ConsultantEventLog_GetEvents.OperationName,
        ConsultantSearchCard_Get.OperationName,
        ConsultantQuality_GetConsultantNew.OperationName
      ],
      onCompleted: () => {
        showSuccess(locale.hsaidCardPaused);
        setLoading(false);
        onClose();
      },
      onError: (error) => {
        showError(error.message, locale.ErrorOccurred);
        setLoading(false);
      }
    });
  };

  const reactivateCard = () => {
    setLoading(true);

    return _reactivateQualityItem({
      variables: {
        consultantId: props.consultantId,
        country: props.country,
        qualityCheckName: props.itemName,
        setOn: new Date()
      },
      refetchQueries: [
        ConsultantEventLog_GetEvents.OperationName,
        ConsultantSearchCard_Get.OperationName,
        ConsultantQuality_GetConsultantNew.OperationName
      ],
      onCompleted: () => {
        showSuccess(locale.hsaidCardReactivated);
        setLoading(false);
        onClose();
      },
      onError: (error) => {
        showError(error.message, locale.ErrorOccurred);
        setLoading(false);
      }
    });
  };

  if (loading && !data) {
    return (
      <div className={classes.root}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <ErrorCard message={error.toString()} title={`Cannot load quality item`} />;
  }

  return (
    <div className={classes.root} data-test="QualityCheckEditor">
      <SpinnerOverlay
        show={loading}
        style={{ position: "absolute", width: "100%", height: "100%" }}
      />

      <div className={classes.wrapper}>
        <Typography variant="h3" display="block">
          {name}
        </Typography>

        <div className={classes.form}>
          <DevTool control={form.control} />

          <Controller
            name="cardNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                label={locale.CardNumber}
                fullWidth
                error={!!fieldState.error}
                isClearable
                onClear={() => form.setValue(field.name, null, { shouldValidate: true })}
                {...field}
              />
            )}
          />

          <Controller
            name="comment"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                label={locale.hsaidLabel}
                fullWidth
                error={!!fieldState.error}
                isClearable
                onClear={() => form.setValue(field.name, null, { shouldValidate: true })}
                {...field}
              />
            )}
          />

          <Controller
            name="cardType"
            control={form.control}
            render={({ field, fieldState }) => (
              <SingleSelect
                styleProps={{
                  control: { showBorder: true },
                  menu: { showBorder: true }
                }}
                inputLabel={locale.CartType}
                options={cardTypeOptions}
                placeholder=""
                isClearable
                onClear={() => form.setValue(field.name, null, { shouldValidate: true })}
                error={!!fieldState.error}
                {...field}
              />
            )}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 16,
              alignItems: "flex-end"
            }}
          >
            <Controller
              name="date"
              control={form.control}
              render={({ field, fieldState }) => (
                <DatePicker label={locale.Date} error={!!fieldState.error} {...field} />
              )}
            />

            <Controller
              name="sendEmail"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value ?? true}
                      onChange={field.onChange}
                      color="primary"
                    />
                  }
                  label={locale.sendEmailToConsultant}
                />
              )}
            />
          </div>
          {withDocument && (
            <FileUpload
              key={fileUploader.fileUploadResetCount}
              onFileRemove={fileUploader.onFileRemove}
              onFileUpload={fileUploader.onFileUpload}
            />
          )}
        </div>

        <div className={classes.controls}>
          <Button
            variant="text"
            color="primary"
            onClick={onClose}
            style={{ marginRight: "auto" }}
          >
            {locale.Cancel}
          </Button>

          <Grid container spacing={1} justifyContent="flex-end">
            <Grid item>
              <Button variant="outlined" disabled={!data.Status} onClick={clearStatus}>
                {locale.ClearStatus}
              </Button>
            </Grid>

            {canPause && (
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.pauseButton}
                  disabled={!form.formState.isValid}
                  onClick={setPaused}
                >
                  {locale.paused}
                </Button>
              </Grid>
            )}

            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                disabled={!canSave}
                onClick={saveParams}
              >
                {locale.Save}
              </Button>
            </Grid>

            <Grid item>
              {isPaused ? (
                <Button variant="contained" color="secondary" onClick={reactivateCard}>
                  {locale.activate}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!form.formState.isDirty || !form.formState.isValid}
                  onClick={setApproved}
                >
                  {locale.qualityStatuses.Approved}
                </Button>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    minWidth: 700,
    minHeight: 500
  },

  wrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: 36,
    width: "100%"
  },

  spinner: {
    position: "absolute",
    width: "100%",
    height: "100%"
  },

  overlay: {
    borderRadius: theme.shape.borderRadius,
    marginBottom: "0 !important"
  },

  label: {
    textAlign: "center"
  },

  form: {
    ...theme.mixins.childrenMarginBottom(theme.spacing(2)),
    marginTop: 32,
    flex: 1
  },

  controls: {
    ...theme.mixins.childrenMarginRight(theme.spacing(1)),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingTop: 40,
    borderTop: `1px solid ${theme.palette.divider}`
  },
  pauseButton: {
    backgroundColor: Color.Yellow
  }
}));
